// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.Mapping.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * Mapping component
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import constants from 'logic/data/Izenda.CopyManagementConstants';
import Validator from 'share/components/hoc/Izenda.Validator';
import MappingHeader from './Izenda.MappingHeader';
import Panel from 'share/components/controls/Izenda.Panel';
import { Table, Column, ColumnGroup } from 'share/components/controls/DataGrid/Izenda.Table';
import Input from 'share/components/controls/Izenda.Input';
import _Select from 'share/components/controls/multiSelectBox/Izenda.Select';
import _DropDown from 'share/components/controls/Izenda.DropDown';
import { isNil, find, get, getOr, toLower, flatMap, isEmpty, each, join } from 'lodash/fp';

const Select = Validator()(_Select);
const DropDown = Validator()(_DropDown);
const ValidatableInput = Validator()(Input);

@connect()
export default class Mapping extends Component {
  static propTypes = {
    // Workspace detail store
    store: PropTypes.object.isRequired,
    // Workspace detail item
    item: PropTypes.object.isRequired,
    addMapping: PropTypes.func.isRequired,
    dataSource: PropTypes.object.isRequired,
    // Use for Tenant mapping in Advanced Copy
    tenantMode: PropTypes.bool,
    width: PropTypes.number
  };

  render() {
    const { localization, tenantMode, item, addMapping, dataSource } = this.props;
    const TABLE_DEF = tenantMode ? constants.TABLE_DEF.TENANT_MAPPING : constants.TABLE_DEF.GLOBAL_MAPPING;

    const header = {
      component: MappingHeader,
      item,
      tenantMode,
      addMapping,
      localization
    };

    return (
      <Panel header={header} className='izenda-CopyManagementMappings u_borderAll'>
        <Table dataSource={dataSource} minHeight={200} maxHeight={200}>
          <ColumnGroup title={localization.getText('COMMON_LABEL_FROM')}>
            <Column {...TABLE_DEF['fromDatabaseName']} cell={this.renderDatabaseName} />
            <Column {...TABLE_DEF['type']} cell={this.renderType} />
            <Column {...TABLE_DEF['fromObject']} cell={this.renderDataObject} />
          </ColumnGroup>
          <ColumnGroup title={localization.getText('COMMON_LABEL_TO')}>
            <Column {...TABLE_DEF['toDatabaseName']} cell={this.renderDatabaseName} />
            <Column {...TABLE_DEF['toObject']} cell={tenantMode ? this.renderTenantObject : this.renderToObject} />
            {!tenantMode ? <Column {...TABLE_DEF['tenant']} cell={this.renderTenant} /> : null}
          </ColumnGroup>
          <ColumnGroup title='&nbsp;'>
            <Column {...TABLE_DEF['action']} cell={this.renderActionColumn} />
          </ColumnGroup>
        </Table>
      </Panel>
    );
  }

  /*
   * Load Destination connections
   */
  loadDestinationConnections = () => {
    const { store, loader, item } = this.props;
    loader.startLoading();

    item.resetWorkspaceTenantsStatus();
    return store.loadDestinationConnections().finally(loader.endLoading.bind(loader));
  };

  /*
   * Delete mapping item
   */
  deleteMapping = (mappingItem: any) => {
    const { item, dataSource, tenantMode } = this.props;
    const deletedIndex = dataSource.indexOf(mappingItem);
    const deleteCount = 1;

    dataSource.splice(deletedIndex, deleteCount);
    tenantMode && item.selectedTenant.setDirty();

    this.loadDestinationConnections();
  };

  /*
   * Change mapping item
   */
  changeMappingItem = (key: string, mappingItem: any, e: any) => {
    const { validation: valManager, item } = this.props;

    mappingItem[key] = isNil(e.value) ? e.target.value : e.value;
    mappingItem.type == constants.MAPPING_TYPE.Database && this.updateDataObject(key, mappingItem);
    item.markDirty();
    valManager.reset();

    this.loadDestinationConnections();
  };

  /**
   * Update data object when change database name
   */
  updateDataObject = (key: string, mappingItem: any) => {
    const { item, tenantMode } = this.props;
    const selectedDB = item.findSelectedDatabase(mappingItem.fromDatabaseKey) || {};
    const selectedConnection = item.findSelectedConnection(mappingItem.toDatabaseKey);
    item.resetWorkspaceTenantsStatus();

    // Not in tenant mode: only change from Object
    if (!tenantMode) {
      mappingItem.fromObject = selectedDB.name;
    } else if (selectedConnection) {
      mappingItem.fromObject = selectedDB.name;
      mappingItem.toObject = selectedConnection.name;
    }
  };

  /*
   * Render Database column
   */
  renderDatabaseName = (mappingItem: any, key: string, rowIndex: number) => {
    const {
      item: { selectedTenant, mapppingDatabasesSource, distinctConnections },
      tenantMode,
      store
    } = this.props;
    const TABLE_DEF = constants.TABLE_DEF.GLOBAL_MAPPING;
    const isFromDatabase = key === TABLE_DEF.fromDatabaseName.key;
    const options = isFromDatabase
      ? mapppingDatabasesSource
      : tenantMode
        ? selectedTenant.databaseSources
        : distinctConnections;

    const getTenantName = tenantId => {
      const tenantList = get('masterStore.tenantList', store);
      const tenant = find({ id: tenantId || '00000000-0000-0000-0000-000000000000'}, tenantList);
      return tenant != null ? tenant.tenantName : 'Unknown';
    };

    each(option => {
      option.databaseNameTooltip = isFromDatabase || tenantMode
        ? getTenantName(option.tenantId) + ' - ' + option.name
        : join('\n', option.distinctKey);
    }, options);

    const value = isFromDatabase ? mappingItem.fromDatabaseKey : mappingItem.toDatabaseKey;
    const changeMappingItem = this.changeMappingItem;
    const changeDatabaseName = (e: any) => {
      const {fullDBName, databaseServer, databaseUser} = e.additionalValue;

      // Update from/to server
      if (isFromDatabase) {
        mappingItem.fromServer = databaseServer;
        mappingItem.fromDatabaseUser = databaseUser;
      } else {
        mappingItem.toServer = databaseServer;
        mappingItem.toDatabaseUser = databaseUser;
      }

      // Update database name
      e.value = fullDBName;
      changeMappingItem(key, mappingItem, e);
    };

    return (
      <DropDown
        options={options}
        blankOption
        onChange={changeDatabaseName}
        value={value}
        valueField='fullDatabaseKey'
        labelField='fullDBName'
        optionTooltip='databaseNameTooltip'
        field={key + rowIndex}
        additionalKey={key + rowIndex}
        additionalValueField='additionalData'
        validations={constants.REQUIRE_CONSTRAINT}
        group={'mapping' + rowIndex}
      />
    );
  };

  /*
   * Render Data Object column
   */
  renderDataObject = (mappingItem: any, key: string, rowIndex: number) => {
    const {
      item,
      item: { mapppingDataObjects }
    } = this.props;
    let options = getOr([], mappingItem.fromDatabaseKey, mapppingDataObjects);
    let value = mappingItem[key];
    let disabled = false;

    // For Database type : Disabled dropdown and show database alias name
    if (mappingItem.type == constants.MAPPING_TYPE.Database) {
      var selectedDB = item.findSelectedDatabase(mappingItem.fromDatabaseKey);
      if (selectedDB) {
        options = [{ id: selectedDB.name, name: selectedDB.name }];
        value = selectedDB.name;
        disabled = true;
      }
    }

    var isMatchOption = !!find({ name: value }, options);
    value = isMatchOption ? value : null;

    return (
      <DropDown
        options={options}
        blankOption
        onChange={this.changeMappingItem.bind(this, key, mappingItem)}
        value={value}
        valueField='name'
        labelField='name'
        field={key + rowIndex}
        additionalKey={key + rowIndex}
        disabled={disabled}
        validations={constants.REQUIRE_CONSTRAINT}
        group={'mapping' + rowIndex}
      />
    );
  };

  /*
   * Render Type column
   */
  renderType = (mappingItem: any, key: string, rowIndex: number) => (
    <DropDown
      options={constants.MAPPING_TYPES}
      blankOption
      onChange={this.changeMappingItem.bind(this, key, mappingItem)}
      value={mappingItem[key]}
      additionalKey={key + rowIndex}
      field={key + rowIndex}
      validations={constants.REQUIRE_CONSTRAINT}
      group={'mapping' + rowIndex}
    />
  );

  /*
   * Render To Object column
   */
  renderToObject = (mappingItem: any, key: string, rowIndex: number) => (
    <ValidatableInput
      onChange={this.changeMappingItem.bind(this, key, mappingItem)}
      value={mappingItem[key]}
      className='form-control'
      field={key + rowIndex}
      validations={constants.REQUIRE_CONSTRAINT}
      group={'mapping' + rowIndex}
    />
  );

  /*
   * Render To Object column for Advanced Copy
   */
  renderTenantObject = (mappingItem: any, key: string, rowIndex: number) => {
    const {
      item: { selectedTenant }
    } = this.props;
    let value = toLower(mappingItem[key]);
    let disabled = false;

    const selectedToDataSource = find({ fullDatabaseKey: mappingItem.toDatabaseKey }, selectedTenant.databaseSources);
    let options = selectedToDataSource ? selectedToDataSource.dbSource.querySources : [];
    options = flatMap(item => ({ name: item.name, value: toLower(item.name) }), options);

    // For Database type : Disabled dropdown and show database alias name
    if (mappingItem.type == constants.MAPPING_TYPE.Database && selectedToDataSource) {
      const databaseName = selectedToDataSource.name;
      options = [{ value: databaseName, name: databaseName }];
      if (isEmpty(value) || toLower(value) === toLower(databaseName)) {
        value = databaseName;
      }
      disabled = true;
    }

    const isMatchOption = !!find((option: any) => toLower(option.name) === toLower(value), options);
    value = isMatchOption ? value : null;

    return (
      <DropDown
        options={options}
        blankOption
        onChange={this.changeMappingItem.bind(this, key, mappingItem)}
        value={value}
        valueField='value'
        labelField='name'
        field={key + rowIndex}
        additionalKey={key + rowIndex}
        disabled={disabled}
        validations={constants.REQUIRE_CONSTRAINT}
        group={'mapping' + rowIndex}
      />
    );
  };

  /**
   * Render Tenant Mapping column
   */
  renderTenant = (mappingItem: any, key: string, rowIndex: number) => {
    const {
      store: {
        item: { workspaceTenants }
      }
    } = this.props;

    return (
      <div className='select-box'>
        <Select
          multi
          simpleValue
          options={workspaceTenants}
          labelKey='tenantName'
          valueKey='tenantId'
          noResultsText='No result'
          allowCreate={false}
          onChange={mappingItem.updateMappingTenant.bind(mappingItem)}
          value={mappingItem.mappingTenantIds}
          blankOption={true}
          group={'mapping' + rowIndex}
        />
      </div>
    );
  };

  /*
   * Render Action Column
   */
  renderActionColumn = (mappingItem: any) => {
    const { localization } = this.props;
    return (
      <a onClick={this.deleteMapping.bind(this, mappingItem)} className='action-column'>
        <span title={localization.getText('COMMON_LABEL_DELETE')} className='icon-cancel ' />
      </a>
    );
  };
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/CopyManagement/WorkspaceDetail/Mapping/Izenda.Mapping.js